import { apiClient } from "../client";

/** Which site's FAQs to fetch. Omit to get every FAQ (website app). */
export type FaqType = "astrology" | "shop" | "academy";

/**
 * A /faqs record. Field names are modeled loosely (question/answer with
 * common aliases) since the admin backend's exact shape isn't pinned down —
 * `unwrapFaqList` normalizes whichever variant comes back.
 */
export interface Faq {
  id?: number | string;
  question?: string | null;
  answer?: string | null;
  title?: string | null;
  description?: string | null;
  type?: string | null;
  [key: string]: unknown;
}

export interface FaqListParams {
  type?: FaqType;
}

/** The live envelope uses `success` (same as /gallery), but tolerate `status` or a bare array too. */
export type FaqListResponse =
  | { success?: boolean; status?: string; message?: string; data?: Faq[] | { data?: Faq[] } }
  | Faq[];

export const faqs = {
  /** GET /faqs?type=astrology|shop|academy — omit `type` for the full list. */
  list: (params: FaqListParams = {}) =>
    apiClient.get<FaqListResponse>("/faqs", { params: { ...params } }),
};

/** Normalized item every app consumes, whatever the backend called the fields. */
export interface NormalizedFaq {
  id: number | string;
  question: string;
  answer: string;
  /** Lowercased site bucket (astrology|shop|academy|…), "" when unset. Lets
   * the all-types website list group/limit by type. */
  type: string;
}

/**
 * Flatten a /faqs response — envelope, nested paginator, or bare array —
 * into [{id, question, answer, type}], dropping rows missing either text field.
 */
export function unwrapFaqList(res: FaqListResponse): NormalizedFaq[] {
  const rows: Faq[] = Array.isArray(res)
    ? res
    : Array.isArray(res?.data)
      ? res.data
      : Array.isArray((res?.data as { data?: Faq[] })?.data)
        ? ((res.data as { data?: Faq[] }).data as Faq[])
        : [];

  return rows
    .map((row, i) => ({
      id: row.id ?? i,
      question: String(row.question ?? row.title ?? "").trim(),
      answer: String(row.answer ?? row.description ?? "").trim(),
      type: String(row.type ?? "").trim().toLowerCase(),
    }))
    .filter((row) => row.question && row.answer);
}
